/**
 * PropertyCombobox — Mehrfachauswahl von Apartments mit Suche.
 *
 * Auslöser zeigt die aktuelle Auswahl („Alle Apartments (3)" / „2 ausgewählt"). Geöffnet: Suche,
 * Kurzwege „Alle"/„Keins" und eine Liste mit Kästchen. Klick daneben und Escape schließen. Der
 * Zustand liegt beim Aufrufer (`Set<propertyId>`; leeres Set = keins, `null` = alle).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { I } from '@/shared/icons';
import { cn } from '@/shared/lib/cn';
import { PropertyComboboxPanel } from './PropertyComboboxPanel';

export interface PropertyOption {
  id: string;
  name: string;
}

interface Props {
  options: PropertyOption[];
  /** null = alle ausgewählt (Vorbelegung). Set = ausdrückliche Auswahl. */
  selected: Set<string> | null;
  onChange: (next: Set<string> | null) => void;
  /** Volle Breite mit 48-px-Feld, etwa im Handy-Blatt. */
  wide?: boolean;
}

export function PropertyCombobox({ options, selected, onChange, wide = false }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    // Escape schließt und gibt den Fokus zurück (§12) — vorher blieb die Liste offen und der
    // Fokus hing in einem Feld, aus dem die Tastatur nicht mehr herausfand.
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options;
  }, [options, search]);

  const allSelected = selected === null;
  const selectedCount = selected?.size ?? options.length;
  const triggerLabel = allSelected
    ? `Alle Apartments (${options.length})`
    : selectedCount === 0
      ? 'Keins ausgewählt'
      : selectedCount === 1
        ? (options.find((o) => selected?.has(o.id))?.name ?? '1 ausgewählt')
        : `${selectedCount} ausgewählt`;

  function toggle(id: string) {
    const current = selected ?? new Set(options.map((o) => o.id));
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next.size === options.length ? null : next);
  }

  return (
    <div
      ref={ref}
      // Offen gehört Escape der Liste – ein umgebendes Blatt bleibt stehen (BottomSheet).
      data-own-escape={open ? '' : undefined}
      className={cn('relative', wide && 'w-full')}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="apartment-auswahl"
        className={cn(
          'border-line bg-card hover:bg-card-alt text-ink flex items-center gap-2 border px-3.5 text-[13px] font-semibold transition-colors',
          wide ? 'rounded-control h-12 w-full' : 'rounded-pill h-11',
          open && 'border-forest',
        )}
      >
        <I name="Search" size={14} className="text-ink-soft" aria-hidden />
        <span className="min-w-0 grow truncate text-left">{triggerLabel}</span>
        <I
          name="ChevronDown"
          size={14}
          aria-hidden
          className={cn('text-ink-soft transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <PropertyComboboxPanel
          options={filtered}
          isChecked={(id) => allSelected || selected?.has(id) === true}
          search={search}
          onSearch={setSearch}
          onToggle={toggle}
          onSelectAll={() => onChange(null)}
          onSelectNone={() => onChange(new Set())}
        />
      )}
    </div>
  );
}
